import Vue from 'vue'
import gql from '@my/graphql'
import gqlOrder from '@/graphql/order'
import gqlChange from '@/graphql/change'
import { formatChangeCode } from '@/helpers/packSearch'
import { recordProductQuantity } from '@/helpers/productQuantity'

const PACK_FETCH_LIMIT = 100000

function mapPackOrder (doc) {
  return {
    order: doc.order,
    status: doc.status,
    quantity: recordProductQuantity(doc),
    createdAt: doc.createdAt,
    name: doc.name,
    location: doc.location,
    department: doc.department,
    address: doc.address,
    phones: doc.phones || [],
    brand: doc.brand,
    remitData: doc.remitData || [],
    remitDataGenerated: !!doc.remitDataGenerated,
    dataLocked: !!doc.dataLocked,
    hasNoveltyData: !!doc.hasNoveltyData,
    noveltyData: doc.noveltyData || [],
    hasProductChange: !!doc.hasProductChange,
    productChange: doc.productChange || null,
    conveyor: doc.conveyor,
    methodPay: doc.methodPay,
    cod: doc.cod,
    isWholesaler: doc.isWholesaler,
    isDropshipping: doc.isDropshipping,
    isGift: doc.isGift,
    userPayConveyor: doc.userPayConveyor,
    total: doc.total,
    observations: doc.observations || [],
    deliveryTime: doc.deliveryTime,
    fraudReview: doc.fraudReview || null,
    packMessage: '',
    packMessageType: null
  }
}

function mapPackChange (doc) {
  return {
    change: doc.change,
    order: doc.order,
    status: doc.status,
    quantity: recordProductQuantity(doc),
    createdAt: doc.createdAt,
    name: doc.name,
    location: doc.location,
    department: doc.department,
    address: doc.address,
    phones: doc.phones || [],
    brand: doc.brand,
    remitData: doc.remitData || [],
    remitDataGenerated: !!doc.remitDataGenerated,
    dataLocked: !!doc.dataLocked,
    hasNoveltyData: !!doc.hasNoveltyData,
    noveltyData: doc.noveltyData || [],
    conveyor: doc.conveyor,
    methodPay: doc.methodPay,
    cod: doc.cod,
    isWholesaler: doc.isWholesaler,
    isDropshipping: doc.isDropshipping,
    userPayConveyor: doc.userPayConveyor,
    payManual: doc.payManual || [],
    observations: doc.observations || [],
    originalProducts: doc.originalProducts || [],
    newProducts: doc.newProducts || [],
    packMessage: '',
    packMessageType: null
  }
}

const PACK_ORDER_SYNC_KEYS = [
  'status',
  'quantity',
  'createdAt',
  'name',
  'location',
  'department',
  'address',
  'phones',
  'brand',
  'remitData',
  'remitDataGenerated',
  'dataLocked',
  'hasNoveltyData',
  'noveltyData',
  'hasProductChange',
  'productChange',
  'conveyor',
  'methodPay',
  'cod',
  'isWholesaler',
  'isDropshipping',
  'isGift',
  'userPayConveyor',
  'total',
  'observations',
  'deliveryTime'
]

const PACK_CHANGE_SYNC_KEYS = [
  'status',
  'createdAt',
  'name',
  'location',
  'department',
  'address',
  'phones',
  'brand',
  'remitData',
  'remitDataGenerated',
  'dataLocked',
  'hasNoveltyData',
  'noveltyData',
  'conveyor',
  'methodPay',
  'cod',
  'isWholesaler',
  'isDropshipping',
  'userPayConveyor',
  'payManual',
  'observations',
  'originalProducts',
  'newProducts'
]

function applyPackRecordSync (target, dataUpdated, type) {
  if (!target || !dataUpdated) return

  const keys = type === 'change' ? PACK_CHANGE_SYNC_KEYS : PACK_ORDER_SYNC_KEYS

  keys.forEach(key => {
    if (dataUpdated[key] === undefined) return

    if (key === 'remitDataGenerated' || key === 'dataLocked' || key === 'hasNoveltyData' || key === 'hasProductChange') {
      Vue.set(target, key, !!dataUpdated[key])
      return
    }

    Vue.set(target, key, dataUpdated[key])
  })

  if (type === 'change' && dataUpdated.newProducts !== undefined) {
    Vue.set(target, 'quantity', recordProductQuantity({ change: target.change, newProducts: dataUpdated.newProducts }))
  }
}

function syncPackSearchRecord (state, type, idKey, id, syncFn) {
  if (!state.searchRecord || state.searchRecord.type !== type || !state.searchRecord.data) return
  if (state.searchRecord.data[idKey] !== id) return
  syncFn(state.searchRecord.data)
}

export { mapPackOrder, mapPackChange, PACK_ORDER_SYNC_KEYS, PACK_CHANGE_SYNC_KEYS, applyPackRecordSync }

export default {
  namespaced: true,

  state: {
    activeStatus: null,
    orders: [],
    changes: [],
    selectedOrders: {},
    selectedChanges: {},
    statusCounts: {
      paid: null,
      packed: null
    },
    changeStatusCounts: {
      paid: null,
      packed: null
    },
    loading: false,
    fetchToken: 0,
    searchRecord: null,
    searchLoading: false
  },

  mutations: {
    resetPackView (state) {
      state.activeStatus = null
      state.orders = []
      state.changes = []
      state.selectedOrders = {}
      state.selectedChanges = {}
      state.loading = false
      state.searchRecord = null
      state.searchLoading = false
      Vue.set(state.statusCounts, 'paid', null)
      Vue.set(state.statusCounts, 'packed', null)
      Vue.set(state.changeStatusCounts, 'paid', null)
      Vue.set(state.changeStatusCounts, 'packed', null)
    },

    setPackActiveStatus (state, status) {
      state.activeStatus = status
    },

    setPackLoading (state, loading) {
      state.loading = loading
    },

    bumpPackFetchToken (state) {
      state.fetchToken += 1
    },

    resetPackFetch (state, status) {
      state.orders = []
      state.changes = []
      state.selectedOrders = {}
      state.selectedChanges = {}
      Vue.set(state.statusCounts, status, null)
      Vue.set(state.changeStatusCounts, status, null)
    },

    setPackStatusCount (state, { status, count }) {
      Vue.set(state.statusCounts, status, count)
    },

    setPackChangeStatusCount (state, { status, count }) {
      Vue.set(state.changeStatusCounts, status, count)
    },

    setPackOrders (state, orders) {
      state.orders = orders
    },

    setPackChanges (state, changes) {
      state.changes = changes
    },

    setPackSelected (state, { order, value }) {
      Vue.set(state.selectedOrders, order, value)
    },

    setPackChangeSelected (state, { change, value }) {
      Vue.set(state.selectedChanges, change, value)
    },

    setPackAllSelected (state, { orderIds, value }) {
      orderIds.forEach(order => {
        Vue.set(state.selectedOrders, order, value)
      })
    },

    setPackAllChangesSelected (state, { changeIds, value }) {
      changeIds.forEach(change => {
        Vue.set(state.selectedChanges, change, value)
      })
    },

    clearPackSelection (state) {
      state.selectedOrders = {}
      state.selectedChanges = {}
    },

    setPackOrderMessage (state, { order, packMessage, packMessageType }) {
      const found = state.orders.find(item => item.order === order)
      if (!found) return
      Vue.set(found, 'packMessage', packMessage)
      Vue.set(found, 'packMessageType', packMessageType)
    },

    setPackChangeMessage (state, { change, packMessage, packMessageType }) {
      const found = state.changes.find(item => item.change === change)
      if (!found) return
      Vue.set(found, 'packMessage', packMessage)
      Vue.set(found, 'packMessageType', packMessageType)
    },

    clearPackMessagesForOrders (state, orderIds) {
      const selectedSet = new Set(orderIds)
      state.orders.forEach(orderItem => {
        if (!selectedSet.has(orderItem.order)) return
        Vue.set(orderItem, 'packMessage', '')
        Vue.set(orderItem, 'packMessageType', null)
      })
    },

    clearPackMessagesForChanges (state, changeIds) {
      const selectedSet = new Set(changeIds)
      state.changes.forEach(changeItem => {
        if (!selectedSet.has(changeItem.change)) return
        Vue.set(changeItem, 'packMessage', '')
        Vue.set(changeItem, 'packMessageType', null)
      })
    },

    setPackSearchLoading (state, loading) {
      state.searchLoading = loading
    },

    setPackSearchRecord (state, searchRecord) {
      state.searchRecord = searchRecord
    },

    clearPackSearchRecord (state) {
      state.searchRecord = null
      state.searchLoading = false
    },

    updatePackSearchCart (state, { order, cart }) {
      syncPackSearchRecord(state, 'order', 'order', order, target => {
        Vue.set(target, 'cart', cart)
      })
    },

    updatePackSearchNewProducts (state, { change, newProducts }) {
      syncPackSearchRecord(state, 'change', 'change', change, target => {
        Vue.set(target, 'newProducts', newProducts)
      })
    },

    updatePackSearchStatus (state, message) {
      if (!state.searchRecord || !state.searchRecord.data) return

      if (state.searchRecord.type === 'order') {
        if (!message.order || state.searchRecord.data.order !== message.order) return
        Vue.set(state.searchRecord.data, 'status', message.status)
        return
      }

      if (!message.change || state.searchRecord.data.change !== message.change) return
      Vue.set(state.searchRecord.data, 'status', message.status)
    },

    updatePackOrderFromSocket (state, dataUpdated) {
      if (!dataUpdated || !dataUpdated.order) return

      const found = state.orders.find(item => item.order === dataUpdated.order)
      if (found) {
        applyPackRecordSync(found, dataUpdated, 'order')
      }

      if (state.searchRecord && state.searchRecord.type === 'order' && state.searchRecord.data && state.searchRecord.data.order === dataUpdated.order) {
        applyPackRecordSync(state.searchRecord.data, dataUpdated, 'order')
      }
    },

    updatePackChangeFromSocket (state, dataUpdated) {
      if (!dataUpdated || !dataUpdated.change) return

      const found = state.changes.find(item => item.change === dataUpdated.change)
      if (found) {
        applyPackRecordSync(found, dataUpdated, 'change')
      }

      if (state.searchRecord && state.searchRecord.type === 'change' && state.searchRecord.data && state.searchRecord.data.change === dataUpdated.change) {
        applyPackRecordSync(state.searchRecord.data, dataUpdated, 'change')
      }
    }
  },

  actions: {
    async fetchPackStatusData ({ state, commit }, status) {
      commit('bumpPackFetchToken')
      const token = state.fetchToken
      commit('setPackLoading', true)
      commit('resetPackFetch', status)

      const queryVariables = {
        search: status,
        searchBy: 'status',
        sort: 'createdAt',
        order: '-'
      }

      try {
        const fetchCollection = async (gqlQuery, getter) => {
          let page = 1
          let hasNextPage = true
          let totalDocs = 0
          const docs = []

          while (hasNextPage) {
            const response = await gql.query(gqlQuery, {
              ...queryVariables,
              limit: PACK_FETCH_LIMIT,
              page
            })

            if (!response || token !== state.fetchToken) return null

            const data = response.data[getter]
            totalDocs = data.totalDocs
            docs.push(...data.docs)
            hasNextPage = data.hasNextPage
            page += 1
          }

          return { docs, totalDocs }
        }

        const [ordersResult, changesResult] = await Promise.all([
          fetchCollection(gqlOrder.GET_ALL, 'getOrders'),
          fetchCollection(gqlChange.GET_ALL, 'getChanges')
        ])

        if (token !== state.fetchToken) return

        if (ordersResult) {
          commit('setPackStatusCount', { status, count: ordersResult.totalDocs })
          commit('setPackOrders', ordersResult.docs.map(mapPackOrder))
        }

        if (changesResult) {
          commit('setPackChangeStatusCount', { status, count: changesResult.totalDocs })
          commit('setPackChanges', changesResult.docs.map(mapPackChange))
        }
      } finally {
        if (token === state.fetchToken) {
          commit('setPackLoading', false)
        }
      }
    },

    selectPackStatus ({ commit, dispatch }, status) {
      commit('setPackActiveStatus', status)
      return dispatch('fetchPackStatusData', status)
    },

    async generatePackRemit ({ commit }, { orderIds = [], changeIds = [] }) {
      if ((!orderIds || !orderIds.length) && (!changeIds || !changeIds.length)) return

      if (orderIds.length) commit('clearPackMessagesForOrders', orderIds)
      if (changeIds.length) commit('clearPackMessagesForChanges', changeIds)

      for (const order of orderIds) {
        await generateSinglePackRemit({ commit, order })
      }

      for (const change of changeIds) {
        await generateSinglePackRemit({ commit, change })
      }

      commit('clearPackSelection')
    },

    async fetchPackRecord ({ commit }, parsed) {
      commit('setPackSearchLoading', true)
      commit('setPackSearchRecord', null)

      try {
        let response
        let found

        if (parsed.type === 'order') {
          response = await gql.query(
            gqlOrder.GET_BY_ORDER_ADMIN,
            { order: parsed.queryId },
            false,
            true,
            true
          )
          found = response && response.data && response.data.getByOrderAdmin
        } else {
          response = await gql.query(
            gqlChange.GET_BY_CHANGE_ADMIN,
            { change: parsed.queryId },
            false,
            true,
            true
          )
          found = response && response.data && response.data.getByChangeAdmin
        }

        if (!response) {
          return { success: false }
        }

        if (response.error) {
          return { success: false, response }
        }

        if (!found) {
          return { success: false, notFound: true }
        }

        if (parsed.type === 'order') {
          commit('setPackSearchRecord', {
            type: 'order',
            code: found.order,
            name: found.name,
            brand: found.brand,
            data: found
          })
        } else {
          commit('setPackSearchRecord', {
            type: 'change',
            code: formatChangeCode(found.change),
            name: found.name,
            brand: found.brand,
            data: found
          })
        }

        return { success: true }
      } catch (error) {
        return { success: false, error }
      } finally {
        commit('setPackSearchLoading', false)
      }
    }
  }
}

async function generateSinglePackRemit ({ commit, order, change }) {
  const idKey = order ? 'order' : 'change'
  const id = order || change
  const setMessage = order ? 'setPackOrderMessage' : 'setPackChangeMessage'

  try {
    const errors = []
    const response = await gql.mutate(
      gqlOrder.GENERATE_REMIT_DATA,
      { order, change },
      false,
      errors
    )

    if (!response || response.error) {
      commit(setMessage, {
        [idKey]: id,
        packMessage: formatPackError((response && response.error) || errors[0]),
        packMessageType: 'error'
      })
      return
    }

    applyPackRemitResult({
      commit,
      id,
      result: response.data.generateRemitData,
      setMessage,
      idKey
    })
  } catch (error) {
    commit(setMessage, {
      [idKey]: id,
      packMessage: formatPackError(error),
      packMessageType: 'error'
    })
  }
}

function applyPackRemitResult ({ commit, id, result, setMessage, idKey }) {
  if (!result) {
    commit(setMessage, {
      [idKey]: id,
      packMessage: 'no hubo respuesta',
      packMessageType: 'error'
    })
    return
  }

  if (!result.success) {
    commit(setMessage, {
      [idKey]: id,
      packMessage: result.message || 'error al generar remite',
      packMessageType: 'error'
    })
    return
  }

  commit(setMessage, {
    [idKey]: id,
    packMessage: result.message || 'remite generado',
    packMessageType: 'ok'
  })
}

function formatPackError (error) {
  if (!error) return 'no hubo respuesta del servidor'
  if (typeof error === 'string') return error
  if (error.message) return error.message
  return String(error)
}



// WEBPACK FOOTER //
// ./src/store/modules/pack.js